import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  adminLogin, adminMe, adminLeads, adminSubscribers, adminStats,
  adminUpdateLead, adminDeleteLead, adminChangePassword, adminUpdateNotes, adminDownloadResume, getToken, setToken, clearToken,
} from "../lib/api";
import Logo from "../components/vyft/Logo";
import { Magnetic } from "../components/vyft/primitives";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { LogOut, Loader2, Trash2, Inbox, Users, Trophy, Mail, Search, KeyRound, Download, StickyNote, FileDown, Briefcase } from "lucide-react";
import CareersManager from "./admin/CareersManager";

const STATUSES = ["new", "contacted", "won", "lost"];
const STATUS_STYLE = {
  new: "bg-[#FF1F8E]/15 text-[#FF5B8A] border-[#FF1F8E]/30",
  contacted: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  won: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  lost: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking

  useEffect(() => {
    document.documentElement.classList.add("dark");
    if (!getToken()) { setAuthed(false); return; }
    adminMe().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);

  if (authed === null) {
    return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  return authed ? <Dashboard onLogout={() => { clearToken(); setAuthed(false); }} /> : <Login onLogin={() => setAuthed(true)} />;
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await adminLogin(email, password);
      setToken(token);
      toast.success("Welcome back.");
      onLogin();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background px-6 relative overflow-hidden grain">
      <motion.div className="orb bg-[#FF1F8E] h-80 w-80 -top-20 -left-10" animate={{ x: [0, 30, 0], y: [0, 20, 0] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="orb bg-[#F97316] h-72 w-72 bottom-0 right-0" animate={{ x: [0, -25, 0], y: [0, 25, 0] }} transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }} />
      <motion.form
        initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        onSubmit={submit} data-testid="admin-login-form"
        className="relative z-10 w-full max-w-md rounded-3xl border border-border/10 bg-card p-8 shadow-2xl shimmer-card"
      >
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Logo />
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.6 }} className="mt-8 font-display text-2xl font-bold tracking-tight">
          Admin access
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }} className="mt-1 text-sm text-muted-foreground">
          Sign in to view your leads.
        </motion.p>
        <div className="mt-6 space-y-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.5 }} className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input data-testid="admin-email" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="info@vyft.media" className="rounded-xl bg-background/50 h-12" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.68, duration: 0.5 }} className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input data-testid="admin-password" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="rounded-xl bg-background/50 h-12" />
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.82, duration: 0.5 }} className="mt-6">
          <Magnetic>
            <Button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full rounded-full h-12 bg-brand-gradient text-white border-0 hover:opacity-90 shimmer-card">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </Magnetic>
        </motion.div>
      </motion.form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -4 }}
      className="rounded-2xl border border-border/10 bg-card p-5 shimmer-card"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="mt-2 font-display text-3xl font-bold text-gradient">{value ?? "—"}</div>
    </motion.div>
  );
}

function ChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords do not match."); return; }
    if (next.length < 8) { toast.error("Use at least 8 characters."); return; }
    setLoading(true);
    try {
      await adminChangePassword(cur, next);
      toast.success("Password updated. Use it next time you sign in.");
      setOpen(false); setCur(""); setNext(""); setConfirm("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update password");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-change-password" variant="outline" className="rounded-full border-border/20">
          <KeyRound className="h-4 w-4 mr-1" /> Password
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-3xl">
        <DialogHeader><DialogTitle className="font-display">Change password</DialogTitle>
          <DialogDescription>Set a new password for your admin account.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} data-testid="change-password-form" className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="cur">Current password</Label>
            <Input data-testid="cp-current" id="cur" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="rounded-xl h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="next">New password</Label>
            <Input data-testid="cp-new" id="next" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="rounded-xl h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input data-testid="cp-confirm" id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-xl h-11" />
          </div>
          <DialogFooter>
            <Button data-testid="cp-submit" type="submit" disabled={loading} className="rounded-full bg-brand-gradient text-white border-0 hover:opacity-90">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NotesDialog({ lead, onSaved }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);
  const hasNotes = !!(lead.notes && lead.notes.trim());

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateNotes(lead.id, notes);
      onSaved(lead.id, notes);
      toast.success("Notes saved");
      setOpen(false);
    } catch { toast.error("Could not save notes"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setNotes(lead.notes || ""); }}>
      <DialogTrigger asChild>
        <Button data-testid={`lead-notes-${lead.id}`} variant="ghost" size="icon" className={hasNotes ? "text-primary" : "text-muted-foreground hover:text-foreground"}>
          <StickyNote className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-3xl">
        <DialogHeader><DialogTitle className="font-display">Notes · {lead.name}</DialogTitle>
          <DialogDescription>Private notes — visible only to your team.</DialogDescription>
        </DialogHeader>
        <Textarea
          data-testid={`notes-input-${lead.id}`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Private notes about this lead (call summary, next steps, budget confirmed)..."
          className="mt-2 rounded-xl min-h-[160px]"
        />
        <DialogFooter>
          <Button data-testid={`notes-save-${lead.id}`} onClick={save} disabled={saving} className="rounded-full bg-brand-gradient text-white border-0 hover:opacity-90">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save notes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Dashboard({ onLogout }) {
  const [leads, setLeads] = useState([]);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [l, s, st] = await Promise.all([adminLeads(), adminSubscribers(), adminStats()]);
      setLeads(l); setSubs(s); setStats(st);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await adminUpdateLead(id, status); const st = await adminStats(); setStats(st); }
    catch { toast.error("Update failed"); load(); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await adminDeleteLead(id); toast.success("Lead deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const exportCsv = () => {
    if (!leads.length) { toast.error("No leads to export"); return; }
    const cols = ["name", "email", "phone", "company", "enquiry_type", "service", "budget", "status", "message", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(","), ...leads.map((l) => cols.map((c) => esc(l[c])).join(","))];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vyft-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${leads.length} leads`);
  };

  const filtered = leads.filter((l) =>
    [l.name, l.email, l.company, l.service].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase())
  );
  const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-background/70 border-b border-border/10">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo />
            <Badge variant="outline" className="border-border/20 text-muted-foreground">Dashboard</Badge>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">View site</a>
            <ChangePasswordDialog />
            <Button data-testid="admin-logout" onClick={onLogout} variant="outline" className="rounded-full border-border/20">
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <h1 className="font-display text-3xl font-bold tracking-tight">Leads overview</h1>
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Inbox} label="Total leads" value={stats.total_leads} delay={0.05} />
          <StatCard icon={Mail} label="New" value={stats.new_leads} delay={0.15} />
          <StatCard icon={Trophy} label="Won" value={stats.won_leads} delay={0.25} />
          <StatCard icon={Users} label="Subscribers" value={stats.subscribers} delay={0.35} />
        </div>

        <Tabs defaultValue="leads" className="mt-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <TabsList>
              <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
              <TabsTrigger value="subs" data-testid="tab-subscribers">Subscribers</TabsTrigger>
              <TabsTrigger value="careers" data-testid="tab-careers"><Briefcase className="h-3.5 w-3.5 mr-1" /> Careers</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads..." className="pl-9 rounded-full bg-card h-11" />
              </div>
              <Button data-testid="export-csv" onClick={exportCsv} variant="outline" className="rounded-full border-border/20 shrink-0">
                <Download className="h-4 w-4 mr-1" /> Export
              </Button>
            </div>
          </div>

          <TabsContent value="leads" className="mt-6">
            <div className="rounded-2xl border border-border/10 bg-card overflow-hidden">
              {loading ? (
                <div className="p-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : filtered.length === 0 ? (
                <div className="p-10 text-center text-muted-foreground">No leads yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/10 hover:bg-transparent">
                        <TableHead>Name</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Budget</TableHead>
                        <TableHead>Received</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((l) => (
                        <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className="border-border/10">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{l.name}</span>
                              {l.enquiry_type && l.enquiry_type !== "New project" && (
                                <Badge variant="outline" className="border-primary/30 text-primary text-[10px]">{l.enquiry_type}</Badge>
                              )}
                            </div>
                            {l.company && <div className="text-xs text-muted-foreground">{l.company}</div>}
                            {l.message && <div className="mt-1 text-xs text-muted-foreground max-w-xs truncate">{l.message}</div>}
                          </TableCell>
                          <TableCell className="text-sm">
                            <div>{l.email}</div>
                            {l.phone && <div className="text-muted-foreground">{l.phone}</div>}
                          </TableCell>
                          <TableCell className="text-sm">{l.service || "—"}</TableCell>
                          <TableCell className="text-sm">{l.budget || "—"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmt(l.created_at)}</TableCell>
                          <TableCell>
                            <Select value={l.status || "new"} onValueChange={(v) => changeStatus(l.id, v)}>
                              <SelectTrigger data-testid={`lead-status-${l.id}`} className={`h-8 w-32 rounded-full border ${STATUS_STYLE[l.status || "new"]}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {l.resume_filename && (
                                <Button
                                  data-testid={`lead-resume-${l.id}`}
                                  onClick={() => adminDownloadResume(l.id, l.resume_filename).catch(() => toast.error("Download failed"))}
                                  variant="ghost" size="icon" className="text-primary hover:text-primary" title={`Download ${l.resume_filename}`}
                                >
                                  <FileDown className="h-4 w-4" />
                                </Button>
                              )}
                              <NotesDialog lead={l} onSaved={(id, notes) => setLeads((prev) => prev.map((x) => (x.id === id ? { ...x, notes } : x)))} />
                              <Button data-testid={`lead-delete-${l.id}`} onClick={() => remove(l.id)} variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="subs" className="mt-6">
            <div className="rounded-2xl border border-border/10 bg-card overflow-hidden">
              {subs.length === 0 ? (
                <div className="p-10 text-center text-muted-foreground">No subscribers yet.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/10 hover:bg-transparent">
                      <TableHead>Email</TableHead>
                      <TableHead className="text-right">Subscribed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subs.map((s) => (
                      <TableRow key={s.id} className="border-border/10">
                        <TableCell className="font-medium">{s.email}</TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">{fmt(s.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="careers" className="mt-6">
            <CareersManager />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
